import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse } from "@/api/api";
import { useSuspenseQuery, useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useSelectedOrganizationId } from "@/organization/api";
import { makeOptimisticHandlers } from "@/api/optimistic";
import { InstallationData } from "@/installation/installation";
import { EquipmentDetailData } from "./equipment";

export type EquipmentSiteVisitData = {
  id: string;
  equipment: number;
  visited_by: number;
  visited_by_name: string;
  notes: string | null;
  recording_file: string | null;
  recording_transcript: string | null;
  created_at: string;
  updated_at: string;
};

export type EquipmentManufacturerData = {
  id: number;
  name: string;
};

export type EquipmentModelData = {
  id: number;
  name: string;
  model: string;
  manufacturer: EquipmentManufacturerData;
  manufacturer_id?: number;
  asset_type?: string;
  nameplate_capacity?: {
    magnitude: number;
    units: string;
  };
  [key: string]: any;
};

export const equipmentManufacturers = createQueryKeys('equipment-manufacturers', {
  list: () => ({
    queryKey: ['list'],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page_size', '100');
      const url = `/manufacturers/?${params.toString()}`;
      return await get<PaginatedResponse<EquipmentManufacturerData>>(url);
    },
  }),
  detail: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      return await get<EquipmentManufacturerData>(`/manufacturers/${id}/`);
    },
  }),
});

export const equipmentModels = createQueryKeys('equipment-models', {
  list: (manufacturerId?: number) => ({
    queryKey: ['list', manufacturerId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page_size', '100');
      if (manufacturerId !== undefined) {
        params.set('manufacturer_id', manufacturerId.toString());
      }
      const url = `/models/?${params.toString()}`;
      return await get<PaginatedResponse<EquipmentModelData>>(url);
    },
  }),
  detail: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      return await get<EquipmentModelData>(`/models/${id}/`);
    },
  }),
  refrigerantTypes: () => ({
    queryKey: ['refrigerant-types'],
    queryFn: async () => {
      return await get<Array<{ label: string; value: string }>>('/models/refrigerant_types/');
    },
  }),
});

export const useManufacturers = () => {
  return useQuery(equipmentManufacturers.list());
};

export const useManufacturer = (id: number) => {
  return useSuspenseQuery(equipmentManufacturers.detail(id));
};

export const useModels = (manufacturerId?: number) => {
  return useQuery({
    ...equipmentModels.list(manufacturerId),
    enabled: manufacturerId !== undefined,
  });
};

export const useModel = (id: number) => {
  return useSuspenseQuery(equipmentModels.detail(id));
};

export const useRefrigerantTypes = () => {
  return useQuery(equipmentModels.refrigerantTypes());
};

export type CreateManufacturerInput = {
  name: string;
};

export const useCreateManufacturer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateManufacturerInput) => {
      const res = await apiClient.post('/manufacturers/', data);
      return res.data as EquipmentManufacturerData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentManufacturers._def });
    },
  });
};

export type CreateModelInput = {
  name: string;
  model: string;
  manufacturer_id: number;
  asset_type?: string;
  asset_subtype?: string;
  energy_source?: string;
  nameplate_capacity?: number;
  variable_speed?: boolean;
  refrigerant_type?: string;
  phase_count?: number;
};

export const useCreateModel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateModelInput) => {
      const res = await apiClient.post('/models/', data);
      return res.data as EquipmentModelData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentModels._def });
    },
  });
};

export type ModelSpecLookupInput = {
  manufacturer_name: string;
  model_name: string;
};

export type ModelSpecLookupResult = {
  phase_count: number | null;
  asset_type: string | null;
  refrigerant_type: string | null;
  nameplate_capacity_btu: number | null;
};

export const useLookupModelSpecs = () => {
  return useMutation({
    mutationFn: async (data: ModelSpecLookupInput) => {
      const res = await apiClient.post('/models/lookup-specs/', data);
      return res.data as ModelSpecLookupResult;
    },
  });
};

export const equipment = createQueryKeys('equipment', {
  list: (locationId: number) => ({
    queryKey: ['list', locationId],
    queryFn: () => {
      const params = new URLSearchParams({ location: locationId.toString(), page_size: '200' });
      return get<PaginatedResponse<EquipmentDetailData>>(`equipment/?${params}`);
    },
  }),
  listBySublocation: (sublocationId: number) => ({
    queryKey: ['list-sublocation', sublocationId],
    queryFn: () => {
      const params = new URLSearchParams({ sublocation: sublocationId.toString(), page_size: '200' });
      return get<PaginatedResponse<EquipmentDetailData>>(`equipment/?${params}`);
    },
  }),
  detail: (equipmentId: number) => ({
    queryKey: [equipmentId],
    queryFn: () => get<EquipmentDetailData>(`equipment/${equipmentId}/`),
    contextQueries: {
      installations: {
        queryKey: null,
        queryFn: () =>
          get<PaginatedResponse<InstallationData>>(
            `equipment/${equipmentId}/installations/`
          ),
      },
      siteVisits: {
        queryKey: null,
        queryFn: () =>
          get<PaginatedResponse<EquipmentSiteVisitData>>(
            `equipment/${equipmentId}/site-visits/`
          ),
      },
    },
  }),
});

export const useEquipmentList = (locationId: number) =>
  useSuspenseQuery(equipment.list(locationId));

export const useEquipmentBySublocation = (sublocationId: number) =>
  useSuspenseQuery(equipment.listBySublocation(sublocationId));

export const useEquipment = (equipmentId: number) =>
  useSuspenseQuery(equipment.detail(equipmentId));

export const useEquipmentInstallations = (equipmentId: number) =>
  useSuspenseQuery(equipment.detail(equipmentId)._ctx.installations);

export const useEquipmentSiteVisits = (equipmentId: number) =>
  useSuspenseQuery(equipment.detail(equipmentId)._ctx.siteVisits);

export type CreateSiteVisitInput = {
  equipmentId: number;
  notes?: string;
  recording?: { blob: Blob; mimeType: string };
  transcript?: string;
};

export const useCreateSiteVisit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ equipmentId, notes, recording, transcript }: CreateSiteVisitInput) => {
      const formData = new FormData();
      if (notes) formData.append('notes', notes);
      if (recording) {
        const ext = recording.mimeType.split(';')[0].split('/')[1];
        formData.append('recording_file', recording.blob, `recording.${ext}`);
      }
      if (transcript) formData.append('recording_transcript', transcript);

      const res = await apiClient.post(
        `equipment/${equipmentId}/site-visits/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return res.data as EquipmentSiteVisitData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipment._def });
    },
  });
};

export const useRenameEquipment = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: {
      equipment_id: number;
      sublocation_id: number;
      location_id: number;
      name: string;
    }) => {
      const res = await apiClient.patch(
        `organizations/${selectedOrganizationId}/locations/${data.location_id}/sublocations/${data.sublocation_id}/equipment/${data.equipment_id}/`,
        { name: data.name }
      );
      return res.data as EquipmentDetailData;
    },
    ...makeOptimisticHandlers<EquipmentDetailData, { equipment_id: number; sublocation_id: number; location_id: number; name: string }>({
      queryClient,
      rootDef: equipment._def,
      detailDef: equipment.detail._def,
      listDef: equipment.list._def,
      getId: (vars) => vars.equipment_id,
      updater: (old, vars) => ({ ...old, name: vars.name }),
    }),
  });
};

export const useDeleteEquipment = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: { equipment_id: number; sublocation_id: number; location_id: number }) => {
      await apiClient.delete(
        `organizations/${selectedOrganizationId}/locations/${data.location_id}/sublocations/${data.sublocation_id}/equipment/${data.equipment_id}/`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipment._def });
    },
  });
};
